import {
  EngagementStatus,
  InvoiceStatus,
  IPayeeEngagementResponse,
  IPayeeResponse
} from "@wingspanhq/payments/dist/interfaces";
import { WSActionsMenuItem } from "@wingspanhq/fe-component-library/dist/lib/components/WSActions/WSActions";
import { paymentsService } from "../../../services/payments";
import { PayerPayeeEngagementStatus } from "@wingspanhq/payments/dist/interfaces/payerPayeeEngagement";
import { useArchiveEngagementModal } from "../modals/useArchivalEngagementModal";
import { useDeleteEngagementModal } from "../modals/useDeletionEngagementModal";
import { useArchivalNotAllowedModal } from "../modals/useArchivalNotAllowedModal";
import { useDeletionNotAllowedModal } from "../modals/useDeletionNotAllowedModal";
import { useRestoreEngagementModal } from "../modals/useRestorationEngagementModal";
import { useRestorationNotAllowedModal } from "../modals/useRestorationNotAllowedModal";
import { getEngagement } from "../../../services/engagements";
import { useXWingspanPayerId } from "../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import { listWorkLogs } from "../../../services/workLog";

type Props = {
  payee: IPayeeResponse;
  engagement: IPayeeEngagementResponse;
};

export const useEngagementDefaultActions = () => {
  const archiveEngagementModal = useArchiveEngagementModal();
  const deleteEngagementModal = useDeleteEngagementModal();
  const archivalNotAllowedModal = useArchivalNotAllowedModal();
  const deletionNotAllowedModal = useDeletionNotAllowedModal();
  const restoreEngagementModal = useRestoreEngagementModal();
  const restorationNotAllowedModal = useRestorationNotAllowedModal();

  const xWingspanPayerId = useXWingspanPayerId();

  return async (props: Props) => {
    const actions: WSActionsMenuItem[] = [];

    const { summary: activePayablesSummary } =
      await paymentsService.payable.list({
        filter: {
          memberClientId: props.engagement.payerPayeeEngagementId,
          status: {
            in: [
              InvoiceStatus.Pending,
              InvoiceStatus.Open,
              InvoiceStatus.Overdue
            ]
          }
        },
        page: {
          size: 1
        }
      });

    const { summary: relatedPayablesSummary } =
      await paymentsService.payable.list({
        filter: {
          memberClientId: props.engagement.payerPayeeEngagementId
        },
        page: {
          size: 1
        }
      });

    const engagementType = props.engagement.engagementId
      ? await getEngagement(props.engagement.engagementId!, xWingspanPayerId)
      : undefined;

    const workLogs = await listWorkLogs({
      "filter[payerPayeeEngagementId][=]":
        props.engagement.payerPayeeEngagementId
    });

    const hasActivePayables = activePayablesSummary.listSize > 0;
    const hasAnyPayables = relatedPayablesSummary.listSize > 0;
    const engagementTypeIsActive =
      engagementType?.status === EngagementStatus.Active;
    const hasActiveLogs = workLogs.length > 0;

    if (props.engagement.status === PayerPayeeEngagementStatus.Active) {
      if (hasActivePayables) {
        actions.push({
          disabled: true,
          onDisabledClick: async () => {
            await archivalNotAllowedModal.open({
              payee: props.payee,
              engagement: props.engagement
            });
          },
          label: "Archive",
          icon: "archive"
        });
      } else {
        actions.push({
          onClick: async () => {
            await archiveEngagementModal.open({
              payee: props.payee,
              engagement: props.engagement
            });
          },
          label: "Archive",
          icon: "archive"
        });
      }
    } else {
      if (engagementTypeIsActive || !props.engagement.engagementId) {
        actions.push({
          onClick: async () => {
            await restoreEngagementModal.open({
              payee: props.payee,
              engagement: props.engagement
            });
          },
          label: "Restore",
          icon: "redo"
        });
      } else {
        actions.push({
          disabled: true,
          onDisabledClick: async () => {
            await restorationNotAllowedModal.open({
              payee: props.payee,
              engagement: props.engagement
            });
          },
          label: "Restore",
          icon: "redo"
        });
      }
    }

    if (hasAnyPayables || hasActiveLogs) {
      actions.push({
        disabled: true,
        onDisabledClick: async () => {
          await deletionNotAllowedModal.open({
            payee: props.payee,
            engagement: props.engagement
          });
        },
        label: "Delete",
        icon: "trash"
      });
    } else {
      actions.push({
        onClick: async () => {
          await deleteEngagementModal.open({
            payee: props.payee,
            engagement: props.engagement
          });
        },
        label: "Delete",
        icon: "trash",
        destructive: true
      });
    }

    return actions;
  };
};
